import { useCallback, useEffect, useRef } from "react";
import { isMobile, trackPixelEvent } from "./utils";

// Pages where the widget may pop open by itself
const ELIGIBLE = [
  /^\/$/, /^\/home$/, /^\/tours$/, /^\/tours\/[^/]+$/, /^\/wellness$/, /^\/wellness\/[^/]+$/,
  /^\/hotels$/, /^\/hotel\/property\/[^/]+$/, /^\/packages$/, /^\/bookCabs$/, /^\/sharedcabs$/, /^\/fullcars$/,
];
const SECTION_BY_PATH = { "/tours": "tours-section", "/wellness": "wellness-programs" };

const DISMISSED = "tgh_chat_auto_dismissed";
const SHOWN = "tgh_chat_auto_shown";

/**
 * Opens the widget after: time on page, a fast scroll, or reaching a section.
 * Once per browser session, never after the user closed it, never while typing elsewhere.
 */
export default function useAutoOpen({ pathname, enabled, onOpen }) {
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const markDismissed = useCallback(() => sessionStorage.setItem(DISMISSED, "1"), []);

  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem(DISMISSED) === "1" || sessionStorage.getItem(SHOWN) === "1") return;
    if (!ELIGIBLE.some((re) => re.test(pathname))) return;

    const mobile = isMobile();
    const delayMs = mobile ? 6000 : 8000;
    const velocityThreshold = mobile ? 3500 : 1400;

    let fired = false;
    const timers = [];
    let observer = null;

    const fire = (reason) => {
      if (fired) return;
      const active = document.activeElement;
      if (active && active.matches && active.matches("input, textarea")) return; // user is typing somewhere
      fired = true;
      sessionStorage.setItem(SHOWN, "1");
      onOpenRef.current?.(reason);
      trackPixelEvent("ChatbotAutoOpen", { trigger: reason, page: pathname });
    };

    timers.push(setTimeout(() => fire("time_on_page"), delayMs));

    let lastY = window.scrollY;
    let lastT = performance.now();
    const onScroll = () => {
      const now = performance.now();
      const dt = (now - lastT) / 1000;
      const dy = Math.abs(window.scrollY - lastY);
      if (dt > 0 && dy / dt > velocityThreshold) fire("rapid_scroll");
      lastY = window.scrollY;
      lastT = now;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const sectionId = Object.entries(SECTION_BY_PATH).find(([prefix]) => pathname.startsWith(prefix))?.[1];
    if (sectionId) {
      let attempts = 0;
      const trySetup = () => {
        const target = document.getElementById(sectionId);
        if (target && "IntersectionObserver" in window) {
          observer = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && fire("section_reached")),
            { threshold: 0.4 }
          );
          observer.observe(target);
        } else if (attempts++ < 10) {
          timers.push(setTimeout(trySetup, 300));
        }
      };
      trySetup();
    }

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("scroll", onScroll);
      observer?.disconnect();
    };
  }, [pathname, enabled]);

  return { markDismissed };
}
